import React from 'react';
import {
  Box,
  HStack,
  VStack,
  Text,
  Badge,
  Checkbox,
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  useColorModeValue,
  useToast,
  Tooltip,
  Icon,
} from '@chakra-ui/react';
import { FiMoreVertical, FiEdit2, FiTrash2, FiExternalLink, FiClock, FiUser } from 'react-icons/fi';
import moment from 'moment';
import { useHistory } from 'react-router-dom';
import { useContactDrawer } from '../../contexts/ContactDrawerContext';
import { supabase } from '../../utils/supabase';

const PRIORITY_COLORS = {
  urgent: 'red',
  high: 'orange',
  medium: 'yellow',
  low: 'gray',
};

const TYPE_LABELS = {
  crm: 'CRM',
  loan: 'Loan',
  marketing: 'Marketing',
  custom: 'Custom',
};

export default function TaskCard({ task, onComplete, onReopen, onEdit, onDelete, onClick, getUserName, showAssignee = false }) {
  const history = useHistory();
  const toast = useToast();
  const contactDrawer = useContactDrawer();
  const bg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const hoverBg = useColorModeValue('gray.50', 'gray.700');

  // Handle both Firestore Timestamps and JS Dates
  const getDueMoment = (dueDate) => dueDate ? moment(dueDate.toDate ? dueDate.toDate() : dueDate) : null;
  
  const isOverdue = task.dueDate && getDueMoment(task.dueDate)?.isBefore(moment(), 'day') && task.status !== 'completed';
  const isToday = task.dueDate && getDueMoment(task.dueDate)?.isSame(moment(), 'day');
  const isCompleted = task.status === 'completed';

  const handleNavigateToRelated = async () => {
    if (!task.relatedTo) return;
    
    if (task.relatedTo.type === 'deal') {
      // The deal may have been deleted since the task was created — verify it
      // still exists before navigating so we never land on "deal not found".
      let dealExists = true;
      try {
        const { data, error } = await supabase
          .from('crm_deals')
          .select('id')
          .eq('id', task.relatedTo.id)
          .maybeSingle();
        if (!error) dealExists = Boolean(data);
      } catch (err) {
        // Lookup failed (e.g. offline) — fall through with the old behavior.
      }

      if (dealExists) {
        history.push(`/crm/deals/${task.relatedTo.id}`);
      } else if (task.contactId && contactDrawer?.openContactDrawer) {
        contactDrawer.openContactDrawer(task.contactId);
        toast({
          title: "This lead's deal no longer exists",
          description: 'Opened the contact instead.',
          status: 'info',
          duration: 4000,
        });
      } else {
        toast({
          title: 'This record no longer exists',
          description: 'The linked lead was deleted.',
          status: 'warning',
          duration: 4000,
        });
      }
    } else if (task.relatedTo.type === 'contact') {
      // Slide the contact drawer open on the current page; only navigate to
      // the dashboard deep-link if the app-level drawer is unavailable
      if (contactDrawer?.openContactDrawer) {
        contactDrawer.openContactDrawer(task.relatedTo.id);
      } else {
        history.push(`/crm/contacts/${task.relatedTo.id}`);
      }
    } else if (task.relatedTo.type === 'client') {
      history.push(`/in-progress/${task.relatedTo.id}`);
    }
  };

  return (
    <Box
      bg={bg}
      borderWidth="1px"
      borderColor={borderColor}
      borderLeftWidth="4px"
      borderLeftColor={`${PRIORITY_COLORS[task.priority]}.500`}
      borderRadius="md"
      p={4}
      _hover={{ bg: hoverBg, shadow: 'sm', cursor: onClick ? 'pointer' : 'default' }}
      transition="all 0.2s"
      opacity={isCompleted ? 0.6 : 1}
      onClick={() => onClick && onClick(task)}
    >
      <HStack justify="space-between" align="flex-start" gap={2} minW={0}>
        <HStack spacing={3} flex="1 1 0" minW={0}>
          <Checkbox
            isChecked={isCompleted}
            onChange={(e) => {
              e.stopPropagation();
              // Unchecking a completed task reopens it rather than re-completing it.
              // Without onReopen, the caller is expected to branch on status itself.
              if (isCompleted && onReopen) {
                onReopen(task.id);
              } else {
                onComplete?.(task.id);
              }
            }}
            onClick={(e) => e.stopPropagation()}
            colorScheme="green"
            size="lg"
            flexShrink={0}
          />
          
          <VStack align="flex-start" spacing={1} flex="1 1 0" minW={0}>
            <Text
              fontWeight="semibold"
              fontSize="md"
              noOfLines={2}
              textDecoration={isCompleted ? 'line-through' : 'none'}
            >
              {task.title}
            </Text>
            
            {task.description && (
              <Text fontSize="sm" color="gray.600" noOfLines={2}>
                {task.description}
              </Text>
            )}

            <HStack spacing={2} flexWrap="wrap" gap={1} minW={0}>
              {/* Assignee Badge - shown when viewing multiple users */}
              {showAssignee && task.assignedTo && getUserName && (
                <Tooltip label="Assigned to">
                  <Badge size="sm" colorScheme="teal" display="flex" alignItems="center" gap={1}>
                    <Icon as={FiUser} boxSize={2.5} />
                    {getUserName(task.assignedTo)}
                  </Badge>
                </Tooltip>
              )}

              {task.type && (
                <Badge size="sm" colorScheme="blue">
                  {TYPE_LABELS[task.type] || task.type}
                </Badge>
              )}
              
              {task.relatedTo && (
                <Tooltip label={`Click to open ${task.relatedTo.type}`}>
                  <Badge
                    size="sm"
                    colorScheme="purple"
                    cursor="pointer"
                    maxW="100%"
                    overflow="hidden"
                    textOverflow="ellipsis"
                    whiteSpace="nowrap"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleNavigateToRelated();
                    }}
                  >
                    {task.relatedTo.name}
                  </Badge>
                </Tooltip>
              )}

              {task.dueDate && (
                <HStack spacing={1}>
                  <Icon as={FiClock} boxSize={3} color={isOverdue ? 'red.500' : 'gray.500'} />
                  <Text
                    fontSize="xs"
                    color={isOverdue ? 'red.500' : isToday ? 'orange.500' : 'gray.600'}
                    fontWeight={isOverdue || isToday ? 'semibold' : 'normal'}
                  >
                    {getDueMoment(task.dueDate)?.format('MMM DD')}
                    {task.dueTime && ` @ ${task.dueTime}`}
                    {isOverdue && ' (Overdue)'}
                    {isToday && ' (Today)'}
                  </Text>
                </HStack>
              )}
            </HStack>
          </VStack>
        </HStack>

        <Menu>
          <MenuButton
            as={IconButton}
            icon={<FiMoreVertical />}
            aria-label="Task actions"
            variant="ghost"
            size="sm"
            flexShrink={0}
            onClick={(e) => e.stopPropagation()}
          />
          <MenuList onClick={(e) => e.stopPropagation()}>
            {onEdit && (
              <MenuItem icon={<FiEdit2 />} onClick={() => onEdit(task)}>
                Edit Task
              </MenuItem>
            )}
            {task.relatedTo && (
              <MenuItem icon={<FiExternalLink />} onClick={handleNavigateToRelated}>
                Open {task.relatedTo.type}
              </MenuItem>
            )}
            {onDelete && (
              <MenuItem icon={<FiTrash2 />} color="red.500" onClick={() => onDelete(task.id)}>
                Delete Task
              </MenuItem>
            )}
          </MenuList>
        </Menu>
      </HStack>
    </Box>
  );
}

